import sha256 from 'crypto-js/sha256';

export default class FeedbackSection {
	static init() {
		const element = document.querySelector('.feedback-section');
		if (!element) {
			return;
		}

        FeedbackSection.armHoneypotGesture(element);

        const entryIdentifier = FeedbackSection.storageKey(element);
        const stored = window.localStorage.getItem(entryIdentifier);
        const hasValidFeedbackStorageItem = !!stored && Number(stored) > Date.now();

        const feedbackInfoElement = element.querySelector('.feedback-info')
        if (window.getComputedStyle(feedbackInfoElement).display === 'none') {
            feedbackInfoElement?.removeAttribute('style')
            element.querySelector('.feedback-result')?.removeAttribute('style')
        } else {
            element.querySelector('.feedback__not-helpful-button')?.classList.remove('active')
            element.querySelector('.feedback-form')?.removeAttribute('style')
        }

        if ( hasValidFeedbackStorageItem ) {
            FeedbackSection.closeFeedbackSection();
            return;
        }

		element.querySelector('.feedback__helpful-button').addEventListener('click', async (e) => {
			if (!e.isTrusted) {
				return;
			}
			const button = e.target;
			const el = button.closest('.feedback-section');
			el.classList.add('feedback-section--form-is-hidden');

            FeedbackSection.setSubmitting(el, true);
            try {
                const nodePath = FeedbackSection.nodePath(el);
                if (!nodePath) {
                    FeedbackSection.showBackendError(el, el.dataset.i18nErrorReload);
                    return;
                }
                const csrfToken = await FeedbackSection.fetchCsrfToken(el);
                if (!await FeedbackSection.ensureHoneypotOrFail(el)) {
                    return;
                }

                const formData = new FormData();
                formData.append('feedback[positive]', true);
                formData.set('node', nodePath);
                formData.append('feedback[text]', '');
                FeedbackSection.appendHoneypotFields(formData, el);
                formData.append('__csrfToken', csrfToken);

                const submissionResponse = await fetch(el.dataset.action, {
                    method: 'POST',
                    body: formData
                });
                await FeedbackSection.handleResponse(el, submissionResponse);
            } catch (err) {
                FeedbackSection.showBackendError(el, el.dataset.i18nErrorGeneric);
            } finally {
                FeedbackSection.setSubmitting(el, false);
            }
		});

		document.querySelector('.feedback__not-helpful-button').addEventListener('click', (e) => {
			const button = e.target;
			const el = button.closest('.feedback-section');
			button.classList.add('active');
			el.classList.remove('feedback-section--form-is-hidden');

			window.scrollTo({
				behavior: 'smooth',
				left: 0,
				top: document.body.scrollHeight
			});

			setTimeout(() => {
				el.querySelector('.feedback-form textarea').focus();
			}, 250);
		});

		document.querySelector('.feedback-section form').addEventListener('submit', FeedbackSection.onSubmit)
	}

	static async onSubmit(e) {
		e.preventDefault();
        const form = e.target;
		const el = e.target.closest('.feedback-section');
		const textarea = el.querySelector('.feedback-form textarea');
		if(!textarea.value || textarea.value.length<5) {
			el.classList.add('feedback-section--form-is-invalid');
			return;
		}
		if (el.classList.contains('feedback-section--is-submitting')) {
			return;
		}

        FeedbackSection.setSubmitting(el, true);
        try {
            const nodePath = FeedbackSection.nodePath(el);
            if (!nodePath) {
                FeedbackSection.showBackendError(el, el.dataset.i18nErrorReload);
                return;
            }
            const csrfToken = await FeedbackSection.fetchCsrfToken(el);
            if (!await FeedbackSection.ensureHoneypotOrFail(el)) {
                return;
            }

            const formData = new FormData(form);
            formData.set('node', nodePath);
            formData.append('__csrfToken', csrfToken);

            const submissionResponse = await fetch(form.getAttribute('action'), {
                method: form.getAttribute('method'),
                body: formData
            });
            await FeedbackSection.handleResponse(el, submissionResponse);
        } catch (err) {
            FeedbackSection.showBackendError(el, el.dataset.i18nErrorGeneric);
        } finally {
            FeedbackSection.setSubmitting(el, false);
        }
	}

    /**
     * Reads the dedup key the way init() does it: the node identifier (UUID)
     * plus the current query string. Both submit paths use this so a write
     * lands at the same key the next page-load reads.
     */
    static storageKey(element) {
        return `feedback-${sha256(element.dataset.nodeIdentifier + window.location.search)}`;
    }

    static async fetchCsrfToken(element) {
        const response = await fetch(element.dataset.csrfTokenEndpoint, { method: 'POST' });
        return response.text();
    }

    /**
     * Returns true if honeypotThree is populated, false otherwise. Tries
     * populateHoneypot once; if the field is still empty (the gesture-fetch
     * failed earlier and the cached promise was cleared), it shows the
     * generic backend error and returns false so callers can bail before
     * sending a submission that will be silently rejected.
     */
    static async ensureHoneypotOrFail(element) {
        await FeedbackSection.populateHoneypot(element);
        const gestureInput = element.querySelector('.feedback-honeypot__gesture');
        if (gestureInput && gestureInput.value) {
            return true;
        }
        FeedbackSection.showBackendError(element, element.dataset.i18nErrorGeneric);
        return false;
    }

    static appendHoneypotFields(formData, element) {
        formData.append('feedback[honeypotOne]', element.querySelector('input[name="feedback[honeypotOne]"]').value);
        formData.append('feedback[honeypotTwo]', element.querySelector('input[name="feedback[honeypotTwo]"]').value);
        formData.append('feedback[honeypotThree]', element.querySelector('input[name="feedback[honeypotThree]"]').value);
    }

    static nodePath(element) {
        const nodePath = (element.dataset.nodePath || element.querySelector('input[name="node"]')?.value || '').trim();
        return ['undefined', 'null'].includes(nodePath.toLowerCase()) ? '' : nodePath;
    }

    static async handleResponse(element, response) {
        if (response.status === 200) {
            const now = new Date();
            window.localStorage.setItem(
                FeedbackSection.storageKey(element),
                (now.setMonth(now.getMonth() + 1)).valueOf()
            );
            element.classList.remove('feedback-section--form-is-invalid', 'feedback-section--form-has-backend-error');
            FeedbackSection.closeFeedbackSection();
            FeedbackSection.focusFeedbackSection();
            return;
        }
        if (response.status === 400) {
            if (await FeedbackSection.responseErrorCode(response) === 'missing-feedback-node') {
                FeedbackSection.showBackendError(element, element.dataset.i18nErrorReload);
                return;
            }
            FeedbackSection.showBackendError(element, element.dataset.i18nErrorValidation);
            return;
        }
        if (response.status === 429) {
            FeedbackSection.showBackendError(element, element.dataset.i18nErrorRateLimit);
            return;
        }
        FeedbackSection.showBackendError(element, element.dataset.i18nErrorGeneric);
    }

    static async responseErrorCode(response) {
        try {
            const body = await response.json();
            return body?.error || null;
        } catch (err) {
            return null;
        }
    }

    static showBackendError(element, message) {
        const target = element.querySelector('.form-backend-error');
        if (target && message) {
            target.innerText = message;
        }
        element.classList.add('feedback-section--form-has-backend-error');
    }

    /**
     * Toggles the submitting state: marks the section, disables both
     * helpful/not-helpful buttons plus the form's submit button, and swaps
     * the submit button label for the "sending" copy so a double-click does
     * not submit twice and the user sees feedback that something is happening.
     */
    static setSubmitting(element, isSubmitting) {
        element.classList.toggle('feedback-section--is-submitting', isSubmitting);
        const buttons = [
            element.querySelector('.feedback__helpful-button'),
            element.querySelector('.feedback__not-helpful-button'),
            element.querySelector('form button[type="submit"]'),
        ].filter(Boolean);
        buttons.forEach(b => { b.disabled = isSubmitting; });

        const submitLabel = element.querySelector('form button[type="submit"] span');
        if (!submitLabel) {
            return;
        }
        const sendingLabel = element.dataset.i18nSending;
        if (isSubmitting && sendingLabel) {
            if (!submitLabel.dataset.origLabel) {
                submitLabel.dataset.origLabel = submitLabel.innerText;
            }
            submitLabel.innerText = sendingLabel;
        } else if (!isSubmitting && submitLabel.dataset.origLabel) {
            submitLabel.innerText = submitLabel.dataset.origLabel;
            delete submitLabel.dataset.origLabel;
        }
    }

    /**
     * Arms a one-shot trusted-gesture listener that lazily populates the
     * gesture-gated honeypot field. populateHoneypot is also callable from
     * any submit path; it dedupes concurrent fetches and lets the next call
     * retry if the previous one failed.
     */
    static armHoneypotGesture(element) {
        const form = element.querySelector('form');
        if (!form) {
            return;
        }
        const gestureInput = form.querySelector('.feedback-honeypot__gesture');
        if (!element.dataset.honeypotTokenEndpoint || !gestureInput) {
            return;
        }

        const events = ['mousemove', 'keydown', 'touchstart', 'touchmove'];
        const onGesture = (event) => {
            if (!event || !event.isTrusted) {
                return;
            }
            events.forEach(eventName => window.removeEventListener(eventName, onGesture, true));
            FeedbackSection.populateHoneypot(element);
        };
        events.forEach(eventName => window.addEventListener(eventName, onGesture, true));
    }

    /**
     * Ensures honeypotThree carries a server-issued token before submission.
     * Only honeypotThree is written; the pre-rendered honeypotTwo is left
     * alone so its age stays >= page-render-time. Concurrent calls share the
     * same in-flight promise; the cache clears on settlement so a follow-up
     * call (e.g. from onSubmit after a failed gesture-fetch) can retry.
     */
    static populateHoneypot(element) {
        const form = element.querySelector('form');
        if (!form) {
            return Promise.resolve();
        }
        const gestureInput = form.querySelector('.feedback-honeypot__gesture');
        const tokenEndpoint = element.dataset.honeypotTokenEndpoint;
        if (!tokenEndpoint || !gestureInput) {
            return Promise.resolve();
        }
        if (gestureInput.value) {
            return Promise.resolve();
        }
        if (element.__honeypotPopulateInflight) {
            return element.__honeypotPopulateInflight;
        }
        element.__honeypotPopulateInflight = (async () => {
            try {
                const response = await fetch(tokenEndpoint, { method: 'POST' });
                if (!response.ok) {
                    return;
                }
                const token = (await response.text()).trim();
                if (!token) {
                    return;
                }
                gestureInput.value = token;
            } catch (e) {
                // Swallow: caller checks gestureInput.value and shows the
                // generic error if it stayed empty.
            } finally {
                delete element.__honeypotPopulateInflight;
            }
        })();
        return element.__honeypotPopulateInflight;
    }

    static closeFeedbackSection() {
        if ($('.feedback-form').is(':visible')) {
            $('.feedback-info').slideUp(400, function () {
                $('.feedback-form').hide();
                $('.feedback__show-form-button').removeClass('active');
                setTimeout(function () {
                    $('.feedback-result').fadeIn(400);
                }, 50)
            });
        } else {
            $('.feedback-info').fadeOut(400);
            $('.feedback-result').fadeIn(400);
        }
    }

    static focusFeedbackSection() {
        if ($(window).width() < 1024) {
            $('html, body').animate({
                scrollTop: $('.feedback-section').offset().top - ($('#header').outerHeight() + 14)
            }, 250);
        }
    }
}
